//#region Libraries
import { useState, useEffect, useReducer } from "react";
//#endregion
//#region Styles
import { ContainerForm, ContentForm, TitleForm } from "components/styles";
//#endregion
//#region Components
import BackToView from "components/BackToView/BackToView";
import HelmetUTP from "components/HelmetUTP/HelmetUTP";
import ToastMessage from "components/ToastMessage/ToastMessage";
import StepIndexer from "components/StepIndexer/StepIndexer";
import Step1 from "./components/Steps/Step1/Step1";
import Step2 from "./components/Steps/Step2/Step2";
import Footer from "./components/Footer/Footer";
import AcadReinforceDetail from "../../components/AcadReinforceDetail/AcadReinforceDetail";
import Preloader from "components/Preloader/Preloader";
//#endregion
//#region Reducer
import reducer, { INITIAL_STATE } from "./utils/reducer/acadReinforce.reducer";
import { TYPES } from "./utils/reducer/acadReinforce.actions";
//#endregion
//#region Services
import { acadReinforcementSessionStorage } from "services/storage/acadReinforcement";
//#endregion
//#region Utils
import ABSOLUTE_PATHS from "routes/utils/absolutePaths";
import { STEPS_DESCRIPTIONS } from "./utils/constants";
import useDataRelatedToForm from "views/RefuerzoAcademico/utils/hooks/useDataRelatedToForm";
import useResumeData from "views/RefuerzoAcademico/utils/hooks/useResumeData";
import useEnvironmentList from "views/RefuerzoAcademico/components/FormCreateAcadReinforce/ManualLoadingForm/utils/hooks/useEnvironmentList";
//#endregion

const Form = ({ loadingRequest, create = false }) => {
	//#region Reducer
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
	//#endregion
	//#region States
	const [canShowDialogLeavingPage, setCanShowDialogLeavingPage] =
		useState(false);
	const [
		loadedDataRelatedToAcadReinforceForm,
		setLoadedDataRelatedToAcadReinforceForm,
	] = useState(false);
	const [mustCorrect, setMustCorrect] = useState(false);
	const [
		hiddenToastMessageSaveAcadReinforce,
		setHiddenToastMessageSaveAcadReinforceAsDraft,
	] = useState(true);
	const [changingStep, setChangingStep] = useState(false);
	//#endregion
	const { currentStep, form, errors, errorTexts } = state;
	const { fetchData, dataRelatedToForm } = useDataRelatedToForm({
		loadingRequest,
		setLoadedDataRelatedToAcadReinforceForm,
	});
	//#region Functiones de interfaz para las acciones con dispatch
	const setForm = form => {
		dispatch({
			type: TYPES.SET_FORM,
			payload: { form },
		});
	};
	const toStep = step => {
		dispatch({ type: TYPES.TO_STEP, payload: { step } });
	};
	const nextStep = payload => {
		dispatch({ type: TYPES.NEXT_STEP, payload });
	};
	const changeForm = (type, entries) => {
		dispatch({
			type,
			payload: { entries },
		});
		setCanShowDialogLeavingPage(true);
	};
	const changeFormStep1 = (part, field, value, errorValue) => {
		changeForm(TYPES.SET_FORM_STEP_1, { part, field, value, errorValue });
	};
	const changeFormStep2 = (part, field, value, errorValue) => {
		changeForm(TYPES.SET_FORM_STEP_2, { part, field, value, errorValue });
	};
	//#endregion
	const acadReinforceResumeData = useResumeData({
		loadingRequest,
		dataRelatedToForm,
		setForm,
		toStep,
		loadedDataRelatedToAcadReinforceForm,
	});
	const environmentList = useEnvironmentList({
		loadingRequest,
		acadReinforceResumeData: acadReinforceResumeData.value,
		form,
		changeFormStep2,
		changingStep: {
			value: changingStep,
			set: setChangingStep,
		},
	});
	//#region Effects
	useEffect(() => {
		if (create) acadReinforcementSessionStorage.acadReinforceId.remove();
		fetchData();
	}, []);
	//#endregion
	//#region Functions
	const toggleHiddenToastMessageSaveAcadReinforceAsDraft = () => {
		setHiddenToastMessageSaveAcadReinforceAsDraft(
			!hiddenToastMessageSaveAcadReinforce
		);
	};
	const changeErrorText = (field, text) =>
		dispatch({
			type: TYPES.SET_ERROR_TEXT,
			payload: {
				field,
				text,
			},
		});
	//#endregion
	const views = [
		<Step1
			loadingRequest={loadingRequest}
			dataRelatedToForm={dataRelatedToForm}
			form={form.step1}
			changeForm={changeFormStep1}
			errors={errors.step1}
			errorTexts={errorTexts}
		/>,
		<Step2
			dataRelatedToForm={{
				...dataRelatedToForm,
				environmentList,
			}}
			form={form.step2}
			changeForm={changeFormStep2}
			errors={errors.step2}
			errorTexts={errorTexts}
			changeErrorText={changeErrorText}
		/>,
		<AcadReinforceDetail
			dataRelatedToForm={{
				...dataRelatedToForm,
				environmentList,
			}}
			acadReinforceResumeData={acadReinforceResumeData.value}
			currentStep={currentStep}
			toStep={toStep}
			loadedDataAboutAcadReinforceForm={
				loadedDataRelatedToAcadReinforceForm
			}
		/>,
	];
	return (
		<ContainerForm>
			<HelmetUTP
				title={`Ref. A. - Carga Manual | ${
					create ? "Crear" : "Editar"
				}`}
			/>
			<ToastMessage
				message="Hemos guardado tu avance"
				hidden={hiddenToastMessageSaveAcadReinforce}
				toggleHidden={toggleHiddenToastMessageSaveAcadReinforceAsDraft}
			/>
			<ToastMessage
				variant="error"
				message="Te faltan completar campos o existen campos inválidos."
				hidden={!mustCorrect}
				toggleHidden={() => setMustCorrect(!mustCorrect)}
			/>
			<BackToView
				to={ABSOLUTE_PATHS.ACAD_REINFORCE_LIST}
				text="Volver a refuerzos"
			/>
			<StepIndexer
				descriptions={STEPS_DESCRIPTIONS}
				currentStep={currentStep}
			/>
			<TitleForm>Creando refuerzo académico - Carga Manual</TitleForm>
			<ContentForm>
				{views[currentStep - 1] || views[views.length - 1]}
			</ContentForm>
			<Footer
				loadingRequest={loadingRequest}
				setChangingStep={setChangingStep}
				form={form}
				fillAcadReinforceResumeData={acadReinforceResumeData.fill}
				setMustCorrect={value => setMustCorrect(value)}
				createAcadReinforce={create}
				currentStep={currentStep}
				toStep={toStep}
				nextStep={nextStep}
				stepsQuantity={STEPS_DESCRIPTIONS.length}
				setHiddenToastMessageSaveAcadReinforceAsDraft={
					setHiddenToastMessageSaveAcadReinforceAsDraft
				}
				canShowDialogLeavingPage={canShowDialogLeavingPage}
				setCanShowDialogLeavingPage={setCanShowDialogLeavingPage}
				changeErrorsStep={(type, value) =>
					dispatch({ type, payload: { value } })
				}
				changeErrorText={changeErrorText}
			/>
		</ContainerForm>
	);
};

export default Form;
